import { AssessmentStatusEnum, RiskRegisterAssessmentStatusEnum } from "../enums/assessmentStatus.enum";
import { QuantitativeDecisionStatisticEnum } from "../enums/quantitativeDecisionStatistic.enum";
import { ComplianceBasisEnum } from "../enums/complianceBasis.enum";
import { ComplianceStateEnum } from "../enums/complianceState.enum";
import { RiskRegisterControlLinkType } from "../enums/riskRegisterControlLinkType.enum";
import { ControlStatusEnum } from "../enums/control-status.enum";
import { Control } from "./control.model";
import { RiskRegisterCard } from "./risk-register-card.model";
import { OelCorrectionMethod } from "../enums/oelCorrectionMethod.enum";
import { SampleFilterMode } from "../enums/sampleFilterMode.enum";
import { SampleShiftCoverage } from "../enums/sampleShiftType.enum";
import { SampleTypeEnum } from "../enums/sampleType.enum";
import { CensoredDataMethod } from "../enums/censoredDataMethod.enum";
import { SampleCensorType } from "../enums/sampleCensorType.enum";
import { NonDetectExclusionEffect } from "../enums/nonDetectExclusionEffect.enum";
import { DistributionPlotUnavailableReason } from "../enums/distributionPlotUnavailableReason.enum";
import { HazardCategory } from "../enums/hazardCategory.enum";
import { RiskRegisterGenerationStatus } from "../enums/riskRegisterGenerationStatus.enum";
import { DistributionUsed } from "../enums/distributionUsed.enum";

/**
 * Color level configuration with color and threshold bounds
 * Used for both risk levels and likelihood levels
 */
export interface RiskLevel {
    Color: string;
    Thresholds: [number, number];
    Name?: string;
}

/** Alias for RiskLevel for backward compatibility */
export type LikelihoodLevel = RiskLevel;

export interface LikelihoodColorLevel {
    key: number;
    Color: string;
    Name?: string;
    QuantitativeName?: string;
    Description?: string;
    MinSamples?: number;
    ReassessmentFrequencyMonths?: number;
    minQuantThreshold?: number;
    maxQuantThreshold?: number;
    minQualThreshold?: number;
    maxQualThreshold?: number;
}

/**
 * A configured exposure-likelihood band resolved for a report template. Carries every field a
 * template may bind (both label fields, the colour, the %OEL threshold bounds and the assessment
 * metadata) plus the level key, so the template — not the mapper — decides which one to render.
 * `name` is the likelihood label; `quantitativeName` is the label a %OEL banding should use.
 * A row with no resolvable band exposes this shape with empty values rather than a fabricated band.
 */
export interface ResolvedExposureBand {
    key: number | null;
    name: string;
    quantitativeName: string;
    color: string;
    min: number | null;
    max: number | null;
    minSamples: number | null;
    reassessmentFrequencyMonths: number | null;
    description: string;
}

/** The empty band exposed for a row with no resolvable band (censored, no OEL, or no config). */
export const EMPTY_EXPOSURE_BAND: ResolvedExposureBand = {
    key: null,
    name: '',
    quantitativeName: '',
    color: '',
    min: null,
    max: null,
    minSamples: null,
    reassessmentFrequencyMonths: null,
    description: ''
};

/**
 * One sample-filter dimension: a {@link SampleFilterMode} plus the explicit set of values it
 * applies to, and whether the first-class (None) / unspecified bucket is part of that set.
 *
 * Wire contract mirrors the BE-1 (DASH-2970) `SampleFilterDimension<T>` jsonb shape: it round-trips
 * as a nested object (never a JSON string), keys are camelCase, and `values` holds the raw enum ints
 * (or, for the survey-type dimension, the lookup ids). `noneSelected` flags membership in the
 * selected set, not the final include/exclude outcome — `mode` decides whether the selected set is
 * kept or subtracted (so under `AllExcept`, `noneSelected = true` means (None) is excluded). Only
 * meaningful for dimensions whose underlying column is nullable; ignored otherwise.
 *
 * `values` is always a plain array — never a Set/Map — because the RA form deep-clones the entity via
 * `JSON.parse(JSON.stringify(entity))` on save, which would turn a Set/Map into `{}`.
 */
export interface SampleFilterDimension<T> {
    mode: SampleFilterMode;
    values: T[];
    noneSelected: boolean;
}

/**
 * Sample-scoping filters (G19). Round-trips as a nested object (BE-1, DASH-2970). Holds the
 * shift-coverage and sample-type dimensions; the survey-type dimension is a separate GUID-keyed list
 * ({@link RiskAssessmentSurveyTypeFilter}) on {@link RiskAssessment.surveyTypeFilters}.
 *
 * Update semantics (BE-3, DASH-2972) are NOT symmetric with create:
 * - On **create**, omitting `sampleFilters` (or sending `null`/`undefined`) means "no filtering".
 * - On **update**, `sampleFilters == null`/`undefined` means "not supplied — leave the persisted
 *   filters untouched" (BE-3 `OnBeforeUpdate` copies the old value forward). It does NOT clear them.
 *   To explicitly reset a dimension to "no filtering" on update you must send it in full as
 *   `{ mode: SampleFilterMode.All, values: [], noneSelected: false }` — not omit it.
 *
 * Both dimensions are required when `sampleFilters` is present: the BE's `DimensionDiffers` treats a
 * `null` dimension against a non-null persisted value as a change, so a partial write (one dimension
 * omitted) silently clears the omitted dimension and triggers recalculation on a Draft RA — or is
 * rejected with 400 on an Approved RA. A write must always send both dimensions in full.
 */
export interface SampleFilters {
    shiftCoverage: SampleFilterDimension<SampleShiftCoverage>;
    sampleType: SampleFilterDimension<SampleTypeEnum>;
}

/**
 * One survey-type filter row (G19, DASH-2970). `surveyTypeId === null` represents the first-class
 * (None) / unspecified survey-type bucket. `isExcluded` gives the semantics: `false` = "only these"
 * (include this survey type), `true` = "all except" (exclude it). The dimension mode is derived from
 * the row set — no rows means All.
 */
export interface RiskAssessmentSurveyTypeFilter {
    id?: string;
    riskAssessmentId?: string;
    surveyTypeId?: string | null;
    isExcluded: boolean;
}

export class RiskAssessment {
    id: string;
    name: string;
    periodStart: number | Date;
    periodEnd: number | Date;
    // RA lifecycle status (Draft/InReview/Approved/Rejected) — matches the BE RiskAssessment.AssessmentStatus enum.
    // Distinct from RiskRegister.assessmentStatus below, which is the 7-value per-cell RiskRegisterAssessmentStatus.
    assessmentStatus: AssessmentStatusEnum;
    quantitativeDecisionStatistic: QuantitativeDecisionStatisticEnum;
    createdByUserId: string;
    createdDate: number | Date;
    createdByName: string;
    approvedBy: string;
    approvedByName: string;
    approvedDate: number | Date;
    organizationLevelId: string;
    copyFromRiskAssessmentId: string;
    initialQualitativeSelector?: number;
    jurisdictionId: string;
    oelTypeId: string;
    fallbackJurisdictionId: string | null;
    fallbackOelTypeId: string | null;
    enableJurisdictionFallback: boolean;
    enableOelTypeFallback: boolean;
    fallbackToAnyOelType: boolean;
    applyShiftCorrection: boolean;
    correctedOEL?: number;
    totalOelCorrectionFactor?: number;
    oelCorrectionMethod?: OelCorrectionMethod;
    toggleAdvanceOptions?: boolean;
    completionPercent?: number;
    // Async register-generation lifecycle: on create the BE stamps Queued and generates registers in the
    // worker service, so a freshly created assessment carries no registers until this reaches Completed.
    // Existing assessments are backfilled to Completed. generationError is a user-safe diagnostic on Failed.
    generationStatus?: RiskRegisterGenerationStatus;
    generationError?: string | null;
    copyQualitativeScores: boolean = true;
    copyControls: boolean = true;
    copyNotes: boolean = true;
    copyPerSegDateOverrides: boolean = true;
    copyApprovalStatuses: boolean = true;
    copyAssessmentMethod: boolean = true;
    current?: boolean;
    /**
     * Shift-coverage and sample-type scoping filters (G19). Nested object, not a JSON string. See
     * {@link SampleFilters} for the asymmetric create/update semantics: on create, omitting this means
     * "no filtering"; on update, omitting it means "leave the persisted filters untouched" (an explicit
     * reset must send both dimensions in full as `mode: All, values: [], noneSelected: false`).
     * The survey-type dimension lives on {@link surveyTypeFilters}.
     */
    sampleFilters?: SampleFilters;
    /**
     * Survey-type scoping filter rows (G19). Read + write since BE-6 (DASH-2982) restored the read map:
     * a GET now returns the persisted rows (or `null`/absent for older data or no filter), so the survey
     * dimension IS hydrated from this field on edit — see `initSampleFilters()`. On write: `undefined` =
     * "not supplied, leave untouched"; `[]` is a destructive clear (BE deletes all rows and recalculates);
     * a non-empty array replaces the row set.
     */
    surveyTypeFilters?: RiskAssessmentSurveyTypeFilter[];
    /**
     * DASH-3168: the DISTINCT {@link HazardCategory} values present across this assessment's agents,
     * from the BE `RiskAssessmentDto.HazardCategories`. The risk-register renders ONE TAB PER value
     * (in canonical order), so categories with no agents in the assessment produce no tab.
     */
    hazardCategories?: HazardCategory[];
    /**
     * Agents/SEGs steps (add-only). `selectedBy` (0 = Individual Agents, 1 = Agent Groups) and
     * `scopeAgentGroupIds`/`additionalAgentIds`/`resolvedGroupAgents` are FE view state used to build
     * the group-mode selection — none of them reach the API. Only the resolved `scopeAgentIds` and
     * `scopeSegIds` are sent, and only on create; the scope is never editable afterwards.
     */
    selectedBy?: number;
    scopeAgentIds?: string[];
    scopeAgentGroupIds?: string[];
    additionalAgentIds?: string[];
    resolvedGroupAgents?: string[];
    scopeSegIds?: string[];
    /**
     * Shared, per-assessment agent-column order for the register matrix, already reconciled server-side
     * (agents added since the save are appended by name, stale ids dropped). Null/empty = agent name A-Z.
     */
    agentColumnOrder?: string[] | null;
}

export class RiskRegister {
    id: string;
    riskAssessmentId: string;
    segId: string;
    agentId: string;
    numberOfWorkers: number | null;
    numberOfContractors: number | null;
    requiredMethodOfAssessment: number;
    methodOfAssessment: number;
    assessmentStatus: RiskRegisterAssessmentStatusEnum;
    qualitativeExposureScore: number;
    qualitativeFrequencyScore: number;
    qualitativeLikelihoodScore: number;
    quantitativeNumberOfSamples: number;
    requiredNumberOfSamples: number;
    quantitativeMean: number;
    quantitativeGeometricMean: number;
    quantitativeGSD: number;
    quantitativeMVUE: number;
    quantitativeDecisionStatisticValue: number;
    quantitativeBDA: string;
    quantitativeResultPercentOfOEL: number;
    quantitativeExceedenceFraction: number;
    quantitativeLikelihoodScore: number;
    title?: string;
    exposureBandDisplay?: string;
    likelihoodNameDisplay?: string;
    healthRiskRatingDisplay?: string;
    healthRiskRatingTooltip?: string;
    isAnalysed?: boolean;
    assessmentTypeLabel?: string | null;
    approvedDate: number;
    isLogNormal: boolean;
    isNormal: boolean;
    organizationLevelId: string;
    segName?: string;
    segCode?: string | null;
    redirectedFromSegId?: string | null;
    note: string;
    agentName: string;
    consequenceRating: number;
    consequenceRatingComment?: string;
    periodEnd: number;
    periodStart: number;
    convertedQuantitativeGeometricMean?: string;
    convertedQuantitativeDecisionStatisticValue?: string;
    convertedQuantitativeMVUE?: string;
    convertedQuantitativeMean?: string;
    convertedQuantitative95thPercentile?: string;
    // The distribution the engine actually selected, resolved on the backend (int enum on the DTO).
    // wTestOutput reports which distributions fit, not which one was used.
    distributionUsed?: DistributionUsed | null;
    // Translated display label ('Normal' / 'Log Normal') derived from distributionUsed at load.
    distributionUsedLabel?: string | null;
    convertedOEL?: string;
    convertedCorrectedOEL?: string;
    oel: number;
    riskRegisterSamples?: RiskRegisterSample[];
    outlierSamples?: RiskRegisterSample[];
    samples?: number[];
    qqPlot?: QqPlot;
    pdfPlot?: PdfPlot;
    wTestOutput?: string;
    imageSrc?: string;
    statusColor?: string;
    registers?: any[];
    /** DASH-2701: SEG-level controls merged into the report dataList by SegId for the SEG Profile widget. */
    controls?: RiskRegisterControl[];
    /** Pre-computed statistic-cell display values, keyed by StatisticsEnum key (Risk Registers report). */
    statisticValues?: { [statKey: string]: string };
    healthRiskScore: number;
    quantitative95thPercentile: number;
    // DASH-2979 (G19): nullable — null means "not assessed" (distinct from an assessed Low).
    exposureRisk: number | null;
    isCompliant?: boolean | null;
    complianceState: ComplianceStateEnum;
    /**
     * DASH-3163: which EN 689 branch produced the verdict (preliminary vs statistical), so the
     * decision narrative states the actual criterion. Nullable/undefined for legacy registers that
     * have not been recalculated since the backend change.
     */
    complianceBasis?: ComplianceBasisEnum | null;
    residualExposureScore: number;
    residualFrequencyScore: number;
    residualLikelihoodScore: number | null;
    residualNotes?: string;
    residualHealthRisk: number;
    plotParameters: PlotParameters;
    isPostTreatmentControlAdequate: boolean;
    reassessmentFrequencyMonths: number;
    isControlAdequate: boolean;
    // null on update leaves the register's controls unchanged.
    riskRegisterControls: Control[] | ControlPayload[] | null;
    createdByUserId: string;
    riskTreatmentControlIds?: string[];
    riskAssessmentControlIds?: string[];
    agentUnits: number;
    correctedOEL?: number;
    correctedDosePercent?: number;
    baseOEL?: number;
    isShiftCorrectionApplied?: boolean;
    shiftCorrectionFactor?: number;
    isPressureCorrectionApplied?: boolean;
    pressureCorrectionFactor?: number;
    totalOelCorrectionFactor?: number;
    oelCorrectionMethod?: OelCorrectionMethod;
    oelCorrectionParams?: OelCorrectionParams;
    bayesianUseInformativePrior?: boolean;
    bayesianDefaultPriorIsInformative?: boolean;
    bayesianPriorOverrideAuditNote?: string;
    bayesianFramework?: string;
    bayesianUninformativeDistribution?: BayesianCategoryProbability[];
    bayesianInformativeDistribution?: BayesianCategoryProbability[];
    bayesianCategoryProbabilityDistribution?: BayesianPriorCategoryDistribution;
    priorMode?: number;
    bayesianDecisionValue?: number;
    bayesianDecisionStatisticValue?: number;
    bayesianUninformativeGM?: number;
    bayesianUninformativeGSD?: number;
    bayesianInformativeGM?: number;
    bayesianInformativeGSD?: number;
    /**
     * G19/DASH-2977 (FE-3): the non-detect handling method snapshotted onto this register at
     * calculation time (BE `CensoredDataMethod`). The method under which the persisted statistics
     * were actually computed — not the jurisdiction's current setting. Defaults to 0 (LOD/√2).
     */
    censoredDataMethod?: CensoredDataMethod;
    /** G19/DASH-2977: non-detects dropped from the statistical set under `ExcludeNonDetects`. 0 otherwise. */
    excludedNonDetectCount?: number;
    /** G19/DASH-2977: diagnostic-only right-censored (GreaterThan) count. */
    rightCensoredCount?: number;
    /** G19/DASH-2977: escalated high-censoring (>=80%) warning flag. */
    isHighCensoringFraction?: boolean;
    /** G19/DASH-2977: distinct reason the exclusion changed the sample set (BE `NonDetectExclusionEffect`); null when no exclusion. */
    nonDetectExclusionEffect?: NonDetectExclusionEffect | null;
    /**
     * DASH-3340: the data condition that stopped the distribution (Q-Q / log-normal PDF) plots from being
     * drawn on the read path (BE `DistributionPlotUnavailableReason`). Recomputed on every read, never
     * persisted; null means the plots were produced.
     */
    distributionPlotUnavailableReason?: DistributionPlotUnavailableReason | null;
}

export interface RiskRegisterAgentEntry {
    agentId: string;
    title?: string;
    color?: string;
    quantitativeGeometricMean?: number | null;
    quantitativeDecisionStatisticValue?: number | null;
    agentUnits?: number | null;
    [key: string]: string | number | null | undefined;
}

export class TableExtendedRiskRegister extends RiskRegister {
    rowSpan?: number;
    segName?: string;
    displayClass?: boolean;
    selected?: boolean;
    color?: string;
    residualColor?: string;
    residualDisplayScore?: number;
    card?: RiskRegisterCard;
    allRegisters?: (RiskRegister | undefined)[];
    orgLevelName?: string;
    orgLevelTitle?: string;
}

/**
 * Wire contract for RiskRegisterDto.OelCorrectionParams. The BE serialises this as a JSON
 * *string* using JsonConfig.Storage (JsonNamingPolicy.CamelCase over OelCorrectionParameters.cs:
 * ShiftLengthHours / WeeklyHours / PressureMmHg / JurisdictionId), so the wire keys are camelCase
 * of those. Consumers must JSON.parse the string and read these exact names — do NOT reintroduce
 * `shiftLength`/`pressure`/`jurisdictionSource`, which never appear on the wire.
 */
export interface OelCorrectionParams {
    shiftLengthHours?: number;
    weeklyHours?: number;
    pressureMmHg?: number;
    jurisdictionId?: string;
}

export class ControlPayload {
    controlId: string;
    riskRegisterControlLinkType: RiskRegisterControlLinkType;
}

export class QqPlot {
    empiricalQuantiles: number[];
    theoreticalQuantiles: number[];
    // Index-aligned with empiricalQuantiles. A sample number repeats for a sample with several results and is
    // null when the sample has none.
    sampleNumbers?: (string | null)[];
    censorTypes?: SampleCensorType[];
}

export class PdfPlot {
    pdfValues: number[];
    xValues: number[];
    convertedQuantitativeGeometricMean?: string;
    convertedQuantitativeDecisionStatisticValue?: string;
    convertedOEL?: string;
    riskRegisterSamples?: [];
    samples?: number[];
}

export enum AssessmentMethodEnum {
    Qualitative = 0,
    Quantitative = 1,
    HazardDoesNotApply = 2,
}

export interface PlotParameters {
    gm: number;
    gsd: number;
    p95: number;
}

export interface RiskRegisterSample {
    sampleId: string;
    sampleNumber: string
    surveyName: string;
    sampleCensorType: number;
    isOutlier?: boolean;
    outlierReason?: string | null;
    sampleResults?: number[];
    /**
     * DASH-3335/3337: the value each sample contributed to the statistics after the
     * register's snapshotted CensoredDataMethod was applied, element-wise with
     * `sampleResults`. Arrives already back-converted to dB for noise (BE
     * ApplyNoiseDbDisplay) — do NOT re-apply the dB transform on the frontend.
     * An entry is `null` where ExcludeNonDetects removed that sample from N.
     */
    substitutedSampleResults?: (number | null)[];
    /**
     * The first substituted result converted to the display (agent) unit — the value
     * actually plotted on the sample chart. `null` for a sample excluded from the
     * statistics under ExcludeNonDetects.
     */
    substitutedValue?: number | null;
    startDate?: number;
    value?: string | number;
    unit?: string;
    valueOel?: string | number;
    valueQuantitativeDecisionStatisticValue?: string | number;
    valueQuantitativeGeometricMean?: string | number;
}

export class RiskRegisterControl {
    controlCode: string;
    controlName: string;
    controlType: number;
    controlBucketName: string;
    controlOwnerName: string;
    status: ControlStatusEnum;
    implementationStartDate: string;
    implementationEndDate: string;
    targetImplementationStart: string;
    targetImplementationEnd: string;
    linkType: RiskRegisterControlLinkType;
    // DASH-2700/2701: populated only when the summary is requested with groupBySeg=true, so the SEG
    // Profile report can section controls per SEG. Left undefined for the SEG-agnostic consumers.
    segId?: string;
    agentId?: string;
}

export class ControlsSummaryPayload {
    riskAssessmentIds: string[];
    // Optional: when omitted the BE returns controls of both link types (RiskAssessment + RiskTreatment).
    riskRegisterControlLinkType?: number;
    // DASH-2701: opt-in per-SEG sectioning — one row per (Control, LinkType, SEG, Agent) with SegId/AgentId set.
    groupBySeg?: boolean;
}

export interface BayesianCategoryProbability {
    score?: number;
    category?: string;
    probability?: number;
    color?: string;
}

export interface BayesianPriorCategoryDistribution {
    uninformative?: BayesianCategoryProbability[];
    informative?: BayesianCategoryProbability[];
    qra?: BayesianCategoryProbability[];
}
